import { useMemo } from "react";
import toast from "react-hot-toast";
import { getApiErrorMessages } from "../utils/apiError";

export const toastTheme = {
  duration: 4000,
  style: {
    borderRadius: "16px",
    border: "1px solid #EAECF0",
    background: "#FFFFFF",
    color: "#101828",
    padding: "14px 16px",
    boxShadow: "0 18px 50px rgba(16, 24, 40, 0.16)",
    fontSize: "14px",
    lineHeight: "20px",
  },
  success: {
    iconTheme: {
      primary: "#027A48",
      secondary: "#ECFDF3",
    },
  },
  error: {
    iconTheme: {
      primary: "#D92D20",
      secondary: "#FEF3F2",
    },
  },
  loading: {
    iconTheme: {
      primary: "#61013B",
      secondary: "#FFFFFF",
    },
  },
};

/**
 * A validation failure usually names more than one field. Showing only the
 * first hides the rest, and one toast per field buries the screen — so a
 * multi-error response renders as a single bulleted toast.
 */
const renderMessages = (messages) => {
  if (messages.length === 1) return messages[0];

  return (
    <div>
      <p style={{ fontWeight: 600, marginBottom: "6px" }}>
        Please fix the following:
      </p>
      <ul style={{ margin: 0, paddingLeft: "18px", listStyle: "disc" }}>
        {messages.map((message, index) => (
          <li key={message} style={{ marginTop: index ? "4px" : 0 }}>
            {message}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Several messages need more time on screen than a one-liner.
const durationFor = (messages) =>
  messages.length > 1 ? Math.min(4000 + messages.length * 1500, 10000) : 4000;

export const useToast = () => {
  return useMemo(
    () => ({
      success: (message, options = {}) => toast.success(message, options),
      error: (message, options = {}) => toast.error(message, options),
      /**
       * Several client-side validation failures in one toast, laid out the
       * same way a multi-field API failure is.
       */
      errors: (messages, options = {}) => {
        const list = (Array.isArray(messages) ? messages : [messages]).filter(
          Boolean
        );
        if (!list.length) return undefined;
        return toast.error(renderMessages(list), {
          duration: durationFor(list),
          ...options,
        });
      },
      /**
       * Toasts an API failure. Pass the raw error — extraction, field
       * labelling and multi-error layout are handled here so every screen
       * reports the same backend failure the same way.
       */
      apiError: (error, fallback, options = {}) => {
        const messages = getApiErrorMessages(error, fallback);
        return toast.error(renderMessages(messages), {
          duration: durationFor(messages),
          ...options,
        });
      },
      loading: (message, options = {}) => toast.loading(message, options),
      custom: (renderer, options = {}) => toast.custom(renderer, options),
      dismiss: toast.dismiss,
      remove: toast.remove,
    }),
    []
  );
};
